// Record expense · capital in · drawing — v2 (UI rebuild batch 3; Astra's expense / capital / drawing mockups). Each is
// its own focused page: the owner's movements stand apart from customer money (Astra: "a specialised owner movement is
// visually mixed with the general Record modes"), each with its own title, its account named by its direction
// (Paid from · Received into · Taken from) and a readback naming its effect before the post.
//
// THE STATE MACHINE IS screens/RecordExpense.tsx's, COPIED VERBATIM: the account read and its readState, the party
// search and its two readStates, the payer, the reference minted once per entry and held across a retry (retired on a
// kind change only when nothing can have landed), the three postings and their bodies, the disabled-until rule, the
// posted facts (drawn by the shared Receipt) and the facts it reads. What changed is the JSX: Astra's field grid, Notes
// / Paid to / Who paid behind More, Review before the post, ServerLine for a refusal (the same errorWords sentence),
// and the Last 10 beside the form again (LastTen, restored 1 Nov 2026) — Recent entries is one link after a posting.
import { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { nothingLanded, readWords } from '../../../lib/errors';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, EXPENSE_CATEGORIES, type ExpenseCategory } from '../../../lib/api';
import { tg } from '../../../lib/telegram';
import { ASSETS, makeReference } from '../../../lib/workflow';
import { amountOk, amountProblem } from '../../../lib/amount';
import { RailPicker, type RailValue } from '../../RailPicker';
import { Combobox, type ComboboxOption } from '../../Combobox';
import { rosterCut, usePartySearch } from '../../../lib/roster-cut';
import { AssetPicker } from '../../AssetPicker';
import { Field } from '../../Field';
import { BalanceRows } from './BalanceRows';
import { ErrorLine } from '../../ErrorLine';
import { AccountsDown } from '../../kit';
import { MoreDetails } from '../../MoreDetails';
import { StateLine, ServerLine, TAP } from '../kit';
import { Receipt, ReceiptAmount } from '../Receipt';
import { partyName, partySubtitle, isStale, needsNameReview, rankForQuery, railName, foldAsset, sameAsset } from '../../../lib/naming';
import { displayAsset, fmtAmount, fmtTime } from '../../../lib/format';
import { readState } from '../../../lib/read-state';
import { RECORD_KIND_COPY, type Posted, type RecordKind } from '../../../screens/RecordExpense';
import { AboutFold, FormFoot, FormPage, ReviewButton, ReviewPanel } from './parts';
import { LastTen, LastTenLink, type LastTenProps } from './LastTen';

const REFERENCE_PREFIX: Record<RecordKind, string> = {
  expense: 'EXP',
  capital: 'CAP',
  drawing: 'DRAW',
};

/** Astra's primary per form. */
const REVIEW_LABEL: Record<RecordKind, string> = { expense: 'Review expense', capital: 'Review capital', drawing: 'Review drawing' };
/** The filtered Money movements a posting's "Recent entries" opens (the Last 10 list it replaces, verification 3.9). */
const RECENT_KIND: Record<RecordKind, string> = { expense: 'expense', capital: 'owner', drawing: 'owner' };

export function OwnerFormV2({ kind }: { kind: RecordKind }) {
  const lastTen: LastTenProps = kind === 'expense' ? { kind: 'entries', movementKind: 'expense' }
    : { kind: 'entries', movementKind: 'owner', direction: kind === 'capital' ? 'in' : 'out' };
  const [search] = useSearchParams();
  const [rail, setRail]         = useState<RailValue>(() => {
    const slug = search.get('rail'), asset = search.get('railAsset');
    return slug && asset ? { slug, asset } : { slug: '', asset: '' };
  });
  const [category, setCategory] = useState<ExpenseCategory>(() => {
    const c = search.get('category');
    return c && EXPENSE_CATEGORIES.some((x) => x.value === c) ? (c as ExpenseCategory) : 'other';
  });
  const [partySlug, setPartySlug] = useState('');
  const [amount, setAmount]     = useState(search.get('amount') ?? '');
  const [notes, setNotes]       = useState(search.get('note') ?? '');

  const copy = RECORD_KIND_COPY[kind];
  const isExpense = kind === 'expense';

  // Same query key as Move money / Treasury, so the rail list is already warm.
  const treasury = useQuery({
    queryKey: ['treasury', 'balances'],
    queryFn:  () => api.treasuryBalances(),
  });
  const rows = treasury.data?.rows ?? [];
  const accounts = readState(treasury);

  const parties = usePartySearch({
    base: ['parties', 'dubaiotc'], text: partySlug,
    ask: (q) => api.listParties({ entity: 'dubaiotc', q, with_balances: true }),
    enabled: isExpense && partySlug.length > 0,
  });
  const partyOptions: ComboboxOption[] = useMemo(() =>
    rankForQuery(parties.data?.parties ?? [], partySlug).map((p) => ({
      value:     p.slug,
      label:     partyName(p),
      secondary: partySubtitle(p, { compact: true }),
      stale:     isStale(p),
      review:    needsNameReview(p.display_name),
    }))
  , [parties.data, partySlug]);
  const selectedParty = useMemo(
    () => (parties.data?.parties ?? []).find((p) => p.slug === partySlug),
    [parties.data, partySlug],
  );
  const lookup = readState(parties);
  const listBalances = readState(parties, { balances: true });
  const listBalancesRead = listBalances === 'known';
  const listBalancesFlagged = lookup === 'known' && listBalances === 'failed';

  const amountNum   = Number(amount);
  const amountWords = amountProblem(amount);
  const [payer, setPayer] = useState<'desk' | 'party'>('desk');
  const [partyAsset, setPartyAsset] = useState<string>('AED');
  const partyPaid   = isExpense && payer === 'party';
  const railOk      = partyPaid ? partySlug.trim() !== '' : (rail.slug !== '' && rail.asset !== '');
  const partyNow = (() => {
    const bag = selectedParty?.balances ?? {};
    return bag[foldAsset(partyAsset)] ?? bag[partyAsset] ?? 0;
  })();

  // ONE REFERENCE PER ENTRY (RecordExpense, re-check 5 Sept 2026): held here, cleared on success, retired on a kind
  // change only when nothing landed.
  const refRef = useRef<string | null>(null);
  const [posted, setPosted] = useState<Posted | null>(null);
  // The readback is open (Astra: Review before the post). Layout state only — the post is the same mutation.
  const [review, setReview] = useState(false);
  const submit = useMutation({
    mutationFn: async () => {
      refRef.current ??= makeReference(REFERENCE_PREFIX[kind]);
      const reference = refRef.current;
      const body = {
        treasury_slug: rail.slug,
        amount:        { asset: rail.asset, amount: amountNum },
        reference,
        notes:         notes.trim() || undefined,
      };
      const railRow = rows.find((r) => r.slug === rail.slug && sameAsset(r.asset, rail.asset));
      const facts = { reference, amount: amountNum, asset: rail.asset, where: railName(railRow ?? { slug: rail.slug }), paidByParty: partyPaid };
      if (kind === 'expense') {
        if (partyPaid) {
          const data = await api.recordExpense({
            amount:             { asset: partyAsset, amount: amountNum },
            reference,
            notes:              notes.trim() || undefined,
            category,
            paid_by_party_slug: partySlug.trim(),
          });
          return { data, ...facts, asset: partyAsset, where: selectedParty ? partyName(selectedParty) : partySlug.trim() };
        }
        const data = await api.recordExpense({
          ...body,
          category,
          party_slug: partySlug.trim() || undefined,
        });
        return { data, ...facts };
      }
      if (kind === 'capital') return { data: await api.recordCapital(body), ...facts };
      return { data: await api.recordDrawing(body), ...facts };
    },
    onSuccess: ({ data, ...facts }) => {
      refRef.current = null;
      setPosted({ ...facts, deduped: !!data.deduped });
      setAmount(''); setNotes('');
      setReview(false);
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError:   () => tg()?.HapticFeedback.notificationOccurred('error'),
  });
  useEffect(() => {
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) refRef.current = null;
  }, [kind]);   // eslint-disable-line react-hooks/exhaustive-deps

  const retry = (again: () => unknown) => <button type="button" className="btn-link text-meta" onClick={() => void again()}>Retry</button>;

  // The post's own rule, verbatim (RecordExpense) — the Review button and the confirm both read it.
  const postDisabled = submit.isPending || !amountOk(amount) || !railOk;
  const railRowNow = rows.find((r) => r.slug === rail.slug && sameAsset(r.asset, rail.asset));
  const assetNow = partyPaid ? partyAsset : rail.asset;

  const partyPicker = (
    <Combobox
      value={partySlug}
      onChange={setPartySlug}
      options={partyOptions}
      more={rosterCut(parties.data)}
      placeholder="Search party"
      loading={partySlug.length > 0 && lookup === 'waiting'}
      emptyHint={partySlug.length === 0 ? null : lookup === 'failed' ? 'Parties could not be read.' : undefined}
    />
  );

  const categoryField = isExpense && (
    <Field label="Category" className="col-span-2 lg:col-span-1">
      <select className="select w-full" value={category} onChange={(e) => setCategory(e.target.value as ExpenseCategory)}>
        {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select>
    </Field>
  );
  const amountField = (
    // The account's asset rides on the amount's label (1 Nov 2026 audit, P7: a read-only Asset box showed "—" beside the
    // account until one was picked) — the account picker is the one place it is chosen.
    <Field label="Amount" hint={!partyPaid && rail.asset ? displayAsset(rail.asset) : undefined}>
      <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
      <ErrorLine>{amountWords}</ErrorLine>
    </Field>
  );
  // The asset follows the account (RailPicker returns both): said here, never picked apart from it.
  const assetField = partyPaid ? (
    // The picker's chips need the row on the phone.
    <Field label="Asset" className="col-span-2 lg:col-span-1">
      <AssetPicker value={partyAsset} onChange={setPartyAsset} options={ASSETS} />
    </Field>
  ) : null;
  const whereField = partyPaid ? (
    // The party is the payer here, not an attribution: the posting goes on their balance.
    <Field label="Paid by" hint="goes on their balance" className="col-span-2 lg:col-span-1">
      {partyPicker}
      {selectedParty && listBalancesRead && (
        <BalanceRows lines={[{ asset: partyAsset, now: partyNow, after: amountNum > 0 ? partyNow - amountNum : undefined }]} />
      )}
    </Field>
  ) : (
    <Field label={copy.railLabel} className={isExpense ? 'col-span-2' : 'col-span-2 lg:col-span-1'}>
      {accounts === 'failed' && !treasury.data ? <AccountsDown value={rail} /> : (
        <RailPicker balanceBelow
          value={rail}
          onChange={setRail}
          rows={rows}
          loading={accounts === 'waiting'}
          readFailed={accounts === 'failed'}
          placeholder={kind === 'capital' ? 'Receiving account' : 'Paying account'}
        />
      )}
    </Field>
  );

  const why = submit.isPending ? 'Booking…'
    : !railOk ? (partyPaid ? 'Pick who paid.' : 'Pick an account.')
    : !amountOk(amount) ? 'Enter an amount.'
    : null;

  const facts = [
    { label: 'Desk', value: 'DubaiOTC' },
    { label: 'Read', value: accounts === 'known' && treasury.dataUpdatedAt ? fmtTime(treasury.dataUpdatedAt) : '—' },
  ];

  const form = (
    <>
      {((accounts === 'failed' && !partyPaid) || (isExpense && lookup === 'failed')) && (
        <StateLine
          tone="warn"
          more={readWords(isExpense && lookup === 'failed' ? parties.error : treasury.error)}
          action={retry(() => { if (accounts === 'failed') void treasury.refetch(); if (lookup === 'failed') void parties.refetch(); })}
        >
          {`Couldn’t read ${isExpense && lookup === 'failed' && accounts === 'failed' && !partyPaid ? 'parties and accounts' : isExpense && lookup === 'failed' ? 'parties' : 'the accounts'} — try again.`}
        </StateLine>
      )}
      {partyPaid && listBalancesFlagged && (
        <StateLine tone="warn" action={retry(parties.refetch)}>
          Balances not read — try again.
        </StateLine>
      )}

      {/* ON REVIEW THE FIELDS GIVE WAY to the readback (1 Nov 2026 audit: the whole form stayed with its details repeated
          under it); Edit brings them back, as typed. */}
      {!review && (<>
      {/* Astra's grid: expense — Category | Amount, Asset | Paid from; capital and drawing — Amount | Asset, then the
          account. On the phone the category and the account take the whole row. */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-4 lg:items-start lg:gap-x-4">
        {isExpense ? <>{categoryField}{amountField}{assetField}{whereField}</> : <>{amountField}{assetField}{whereField}</>}
      </div>

      <div className="border-t border-stroke pt-3">
        <MoreDetails label="More">
          <Field label="Notes">
            <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={isExpense ? 'What it was for' : 'Anything to remember'} />
          </Field>
          {isExpense && (
            <Field label="Who paid">
              <div className="flex gap-2 lg:inline-flex lg:w-auto lg:gap-1 lg:rounded-lg lg:border lg:border-stroke lg:bg-panel lg:p-0.5">
                {([['desk', 'Desk account'], ['party', 'A party']] as const).map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setPayer(v)}
                    className={`seg whitespace-nowrap py-2 lg:py-1 ${payer === v ? 'seg-on' : ''}`}>{label}</button>
                ))}
              </div>
            </Field>
          )}
          {/* "Paid to" — an attribution only; the posting is account → expense. */}
          {isExpense && !partyPaid && (
            <Field label="Paid to" hint="optional">{partyPicker}</Field>
          )}
        </MoreDetails>
      </div>
      </>)}

      {review ? (
        <ReviewPanel
          onEdit={() => setReview(false)}
          why={postDisabled ? why : null}
          rows={[
            isExpense && { label: 'Category', value: EXPENSE_CATEGORIES.find((c) => c.value === category)?.label ?? category },
            { label: 'Amount', value: <span className="whitespace-nowrap font-mono tabular">{amountOk(amount) ? fmtAmount(amountNum, assetNow) : '—'} {assetNow ? displayAsset(assetNow) : ''}</span> },
            partyPaid
              ? { label: 'Paid by', value: selectedParty ? partyName(selectedParty) : partySlug.trim() }
              : { label: copy.railLabel, value: rail.slug ? railName(railRowNow ?? { slug: rail.slug }) : '—' },
            isExpense && !partyPaid && partySlug.trim() !== '' && { label: 'Paid to', value: selectedParty ? partyName(selectedParty) : partySlug.trim() },
            notes.trim() !== '' && { label: 'Notes', value: notes.trim() },
          ]}
          // The job's sentence lives in About this job — the review is the readback and the confirm (Astra, 1 Nov 2026).
          confirm={
            <button className="btn-primary w-full lg:w-auto lg:px-6" disabled={postDisabled} onClick={() => submit.mutate()}>
              {submit.isPending ? 'Booking…' : copy.submit}
            </button>
          }
        />
      ) : (
        <FormFoot line={why}>
          <ReviewButton label={REVIEW_LABEL[kind]} disabled={postDisabled} onClick={() => setReview(true)} />
        </FormFoot>
      )}

      {/* words:ok — the books' own sentence for a refusal; a 5xx says it may have posted (ServerLine). */}
      {submit.isError && <ServerLine error={submit.error} />}
      <LastTenLink {...lastTen} title={LAST_TEN_TITLE[kind]} />
      <AboutFold help={copy.about} facts={facts} className="xl:hidden" />
    </>
  );

  // The Last 10 of this kind beside the form (restored 1 Nov 2026); after a posting it is all the aside keeps (Astra).
  const lastTenList = <LastTen {...lastTen} title={LAST_TEN_TITLE[kind]} />;
  // Beside the form only the Last 10 and the explanation; the desk and when the accounts were read sit in its fold
  // (Astra, 1 Nov 2026: no standing "Before confirming" block — the Review button says it).
  const aside = (
    <>
      {lastTenList}
      <AboutFold help={copy.about} facts={facts} />
    </>
  );

  /* THE RECEIPT IN THE FORM'S PLACE (his "screen UI and confirmation msgs!!!!", 1 Nov 2026): what POSTED, the reference,
     "Record another" back to a fresh form, the recent entries. */
  const receipt = submit.isSuccess && posted ? (
    <Receipt
      title={RECEIPT_TITLE[kind]}
      deduped={posted.deduped}
      reference={posted.reference}
      facts={[
        { label: 'Amount', value: <ReceiptAmount value={posted.amount} asset={posted.asset} /> },
        { label: kind === 'capital' ? 'Into' : posted.paidByParty ? 'Paid by' : 'From', value: posted.where },
      ]}
      another={{ label: 'Record another', onClick: () => submit.reset() }}
      movements={`/movements?kind=${RECENT_KIND[kind]}`}
      links={!posted.deduped && <Link to={`/movements?kind=${RECENT_KIND[kind]}`} className={TAP}>Recent entries →</Link>}
    />
  ) : null;

  return <FormPage form={receipt ?? form} aside={receipt ? lastTenList : aside} />;
}

const LAST_TEN_TITLE: Record<RecordKind, string> = { expense: 'Last 10 costs', capital: 'Last 10 capital in', drawing: 'Last 10 drawings' };

const RECEIPT_TITLE: Record<RecordKind, string> = { expense: 'Expense recorded', capital: 'Capital in recorded', drawing: 'Drawing recorded' };
